import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  FaSearch,
  FaMapMarkerAlt,
  FaCheckCircle,
  FaStar,
  FaTruck,
  FaBoxes,
  FaLocationArrow,
} from "react-icons/fa";
import "./FindDealerPage.css";
import SEO from "../components/SEO/SEO";

const categories = [
  {
    name: "cement",
    label: "Cement",
    icon: "🧱",
  },
  {
    name: "steel",
    label: "Steel",
    icon: "🏗️",
  },
  {
    name: "tiles",
    label: "Tiles",
    icon: "🪟",
  },
  {
    name: "interiors",
    label: "Interiors",
    icon: "🛋️",
  },
  {
    name: "paints",
    label: "Paints",
    icon: "🎨",
  },
  {
    name: "electrical",
    label: "Electrical",
    icon: "⚡",
  },
];

export default function FindDealerPage() {
  const navigate = useNavigate();

  const [category, setCategory] = useState("");
  const [search, setSearch] = useState("");

  const [location, setLocation] = useState(null);

  const [locationStatus, setLocationStatus] = useState(
    "Detecting your location..."
  );

  const [loadingLocation, setLoadingLocation] = useState(true);

  /* --------------------------------------------------
      Detect Current Location
  -------------------------------------------------- */

  useEffect(() => {
    if (!navigator.geolocation) {
      setLocationStatus("Location not supported");
      setLoadingLocation(false);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setLocation({
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
        });

        setLocationStatus("Using your current location");

        setLoadingLocation(false);
      },
      () => {
        setLocationStatus(
          "Location unavailable. You can still search."
        );

        setLoadingLocation(false);
      },
      {
        enableHighAccuracy: true,
        timeout: 8000,
        maximumAge: 60000,
      }
    );
  }, []);

  /* --------------------------------------------------
      Search Dealers
  -------------------------------------------------- */

  const handleSearch = () => {
    const params = new URLSearchParams();

    if (category) params.set("category", category);

    if (search.trim()) params.set("search", search.trim());

    if (location) {
      params.set("lat", location.lat);
      params.set("lng", location.lng);
    }

    navigate(`/dealers?${params.toString()}`);
  };

  /* --------------------------------------------------
      ENTER KEY SEARCH
  -------------------------------------------------- */

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  return (
    <>
      <SEO
  title={`Tiles Dealers Near You | BuildNear`}
  description="Find verified tile dealers near your location with ratings and contact details."
  keywords="tiles dealers, tiles suppliers"
  url="https://buildnear.in/search"
/>

    <div className="find-page">

      {/* ==========================================
                HERO
      ========================================== */}

      <section className="find-hero-section">

        <div className="find-hero-overlay">

          <div className="container">

            <div className="row justify-content-center">

              <div className="col-lg-10 text-center">

                <div className="find-hero-content">

                  <span className="find-hero-badge">
                    India's Trusted Building Material Marketplace
                  </span>

                  <h1 className="find-hero-title">

                    Find Verified Building Material Dealers
                    <br />

                    Near Your Location

                  </h1>

                  <p className="find-hero-subtitle">

                    Compare prices, ratings, delivery time and
                    inventory from trusted local suppliers.

                  </p>

                </div>

              </div>

            </div>

          </div>

        </div>

      </section>

      {/* ==========================================
                SEARCH CARD
      ========================================== */}

      <section className="find-search-section">

        <div className="container">

          <div className="find-search-card">

            <div className="row g-3 align-items-end">

              {/* CATEGORY */}

              <div className="col-lg-3">

                <label className="form-label">

                  Category

                </label>

                <select
                  className="form-select"
                  value={category}
                  onChange={(e) =>
                    setCategory(e.target.value)
                  }
                >

                  <option value="">

                    All Categories

                  </option>

                  {categories.map((cat) => (

                    <option
                      key={cat.name}
                      value={cat.name}
                    >

                      {cat.label}

                    </option>

                  ))}

                </select>

              </div>

              {/* SEARCH */}

              <div className="col-lg-6">

                <label className="form-label">

                  Dealer / Area

                </label>

                <input
                  className="form-control"
                  placeholder="Search dealer name or locality..."
                  value={search}
                  onChange={(e) =>
                    setSearch(e.target.value)
                  }
                  onKeyDown={handleKeyPress}
                />

              </div>

              {/* BUTTON */}

              <div className="col-lg-3 d-grid">

                <button
                  className="btn btn-primary find-search-btn"
                  onClick={handleSearch}
                >

                  <FaSearch className="me-2" />

                  Search Dealers

                </button>

              </div>

            </div>
                        {/* LOCATION STATUS */}

            <div className="find-location-banner">

              <div className="find-location-left">

                <FaLocationArrow className="me-2 text-primary" />

                <span>

                  {loadingLocation
                    ? "Detecting your location..."
                    : locationStatus}

                </span>

              </div>

              {location && (

                <span className="find-location-active">

                  <FaMapMarkerAlt className="me-1" />

                  GPS Enabled

                </span>

              )}

            </div>

          </div>

        </div>

      </section>

      {/* ==========================================
                QUICK CATEGORIES
      ========================================== */}

      <section className="find-category-section">

        <div className="container">

          <div className="find-section-heading">

            <h2>Popular Categories</h2>

            <p>

              Quickly browse dealers by category

            </p>

          </div>

          <div className="row g-4">

            {categories.map((cat) => (

              <div
                key={cat.name}
                className="col-6 col-md-4 col-lg-2"
              >

                <div
                  className={`find-category-card ${
                    category === cat.name ? "active" : ""
                  }`}
                  onClick={() => setCategory(cat.name)}
                >

                  <div className="find-category-icon">

                    {cat.icon}

                  </div>

                  <div className="find-category-name">

                    {cat.label}

                  </div>

                </div>

              </div>

            ))}

          </div>

        </div>

      </section>

      {/* ==========================================
                WHY BUILDNEAR
      ========================================== */}

      <section className="find-features-section">

        <div className="container">

          <div className="find-section-heading">

            <h2>

              Why Choose BuildNear?

            </h2>

            <p>

              India's smarter way to discover reliable building material suppliers.

            </p>

          </div>

          <div className="row g-4">

            <div className="col-md-6 col-lg-3">

              <div className="find-feature-card">

                <FaCheckCircle className="find-feature-icon text-success" />

                <h5>

                  Verified Dealers

                </h5>

                <p>

                  Every supplier is verified before listing.

                </p>

              </div>

            </div>

            <div className="col-md-6 col-lg-3">

              <div className="find-feature-card">

                <FaStar className="find-feature-icon text-warning" />

                <h5>

                  Genuine Reviews

                </h5>

                <p>

                  Read authentic reviews from customers.

                </p>

              </div>

            </div>

            <div className="col-md-6 col-lg-3">

              <div className="find-feature-card">

                <FaTruck className="find-feature-icon text-primary" />

                <h5>

                  Fast Delivery

                </h5>

                <p>

                  Compare estimated delivery times.

                </p>

              </div>

            </div>

            <div className="col-md-6 col-lg-3">

              <div className="find-feature-card">

                <FaBoxes className="find-feature-icon text-danger" />

                <h5>

                  Live Inventory

                </h5>

                <p>

                  Know stock availability before contacting dealers.

                </p>

              </div>

            </div>

          </div>

        </div>

      </section>

      {/* ==========================================
                BUILDNEAR STATS
      ========================================== */}

      <section className="find-stats-section">

        <div className="container">

          <div className="row text-center">

            <div className="col-6 col-md-3">

              <div className="find-stat-card">

                <h2>500+</h2>

                <span>

                  Verified Dealers

                </span>

              </div>

            </div>

            <div className="col-6 col-md-3">

              <div className="stat-card">

                <h2>15+</h2>

                <span>

                  Categories

                </span>

              </div>

            </div>

            <div className="col-6 col-md-3">

              <div className="find-stat-card">

                <h2>5K+</h2>

                <span>

                  Customer Reviews

                </span>

              </div>

            </div>

            <div className="col-6 col-md-3">

              <div className="find-stat-card">

                <h2>24x7</h2>

                <span>

                  Dealer Availability

                </span>

              </div>

            </div>

          </div>

        </div>

      </section>

    </div>
            </>
  );

}